/**
 * /time/review (firm admins): pre-bill flags on released time, everything
 * released across the firm, what's ready to export, and the billing IDs
 * exports need. Stays open after the firm
 * turns tracking off so released time can still be approved and exported.
 */
import { Download, Settings } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { useSearchParams } from "react-router-dom";

import { SettingsModal } from "@/components/settings/SettingsModal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { usePrebill, useReviewKpis, useTimeStatus } from "../queries";
import { BillingIdsTab } from "../review/BillingIdsTab";
import { EntriesTab } from "../review/EntriesTab";
import { ExportDialog } from "../review/ExportDialog";
import { ExportsTab } from "../review/ExportsTab";
import { PrebillTab } from "../review/PrebillTab";
import {
  asReviewTab,
  kpiCards,
  RANGES,
  rangeFor,
  type RangeId,
  type ReviewTab
} from "../review/reviewModel";
import type { ReviewKpis } from "../types";
import { todayIso } from "../utils";
import { TimeOff } from "./TimesheetPage";

const KpiStrip: FC<{ kpis?: ReviewKpis }> = ({ kpis }) => {
  if (!kpis) {
    return (
      <div className="flex gap-4 px-5 pb-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 flex-1" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex px-5 pb-4" data-testid="review-kpis">
      {kpiCards(kpis).map((card, i) => (
        <div
          key={card.id}
          className={cn(
            "flex flex-1 flex-col gap-0.5",
            i > 0 && "border-l border-line-2 pl-4"
          )}
        >
          <span className="text-sm text-fg-3">{card.label}</span>
          <span
            className={cn(
              "text-xl font-medium text-fg tabular-nums",
              card.warn && "text-warn"
            )}
            data-testid={`kpi-${card.id}`}
          >
            {card.value}
          </span>
          <span className="text-sm text-fg-3">{card.sub}</span>
        </div>
      ))}
    </div>
  );
};

export const BillingReviewPage: FC = () => {
  const status = useTimeStatus();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<RangeId>("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const kpis = useReviewKpis(status.is_admin);
  const tab = asReviewTab(params.get("tab"));
  const today = todayIso();
  const dates = useMemo(() => rangeFor(range, today), [range, today]);
  const prebill = usePrebill(dates, status.is_admin);

  if (status.isLoading) return <Skeleton className="m-4 h-40" />;
  if (!status.is_admin) return <TimeOff isAdmin={false} />;

  const setTab = (next: ReviewTab) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === "prebill") out.delete("tab");
        else out.set("tab", next);
        return out;
      },
      { replace: true }
    );

  const prebillFlags = (prebill.data ?? []).reduce(
    (n, m) => n + m.errors + m.warnings,
    0
  );
  const prebillBlocked = (prebill.data ?? []).some((m) => m.errors > 0);

  const idIssues =
    (kpis.data?.blocking?.matters ?? 0) +
    (kpis.data?.blocking?.timekeepers ?? 0);

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface"
      data-testid="billing-review-page"
    >
      <div className="flex h-13 shrink-0 items-center gap-2 pr-4 pl-5">
        <h1 className="mr-1 text-lg font-medium text-fg">Billing Review</h1>
        <Select value={range} onValueChange={(v) => setRange(v as RangeId)}>
          <SelectTrigger size="sm" aria-label="Dates" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!status.enabled ? (
          <StatusBadge tone="warn" data-testid="review-tracking-off">
            Time tracking is off · nothing new is captured
          </StatusBadge>
        ) : null}
        <span className="flex-1" />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setSettingsOpen(true)}
        >
          <Settings /> Time &amp; Billing Settings
        </Button>
        <Button
          variant={tab === "prebill" ? "secondary" : "primary"}
          size="sm"
          onClick={() => setExportOpen(true)}
        >
          <Download /> Export
        </Button>
      </div>

      <KpiStrip kpis={kpis.data} />

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(asReviewTab(v))}
        className="min-h-0 flex-1 gap-0"
      >
        <div className="flex h-11 shrink-0 items-center border-b border-line-2 px-4">
          <TabsList variant="pane">
            <TabsTrigger value="prebill">
              Pre-Bill
              {prebillFlags ? (
                <StatusBadge
                  tone={prebillBlocked ? "danger" : "warn"}
                  className="h-4.5 px-1.5"
                  data-testid="prebill-count"
                >
                  {prebillFlags}
                </StatusBadge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="entries">All Entries</TabsTrigger>
            <TabsTrigger value="ids">
              Billing IDs
              {idIssues ? (
                <StatusBadge tone="warn" className="h-4.5 px-1.5">
                  {idIssues}
                </StatusBadge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="exports">Exports</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="prebill" className="flex min-h-0 flex-col">
          <PrebillTab range={dates} onOpenAll={() => setTab("entries")} />
        </TabsContent>
        <TabsContent value="entries" className="flex min-h-0 flex-col">
          <EntriesTab range={dates} kpis={kpis.data} />
        </TabsContent>
        <TabsContent value="ids" className="flex min-h-0 flex-col">
          <BillingIdsTab />
        </TabsContent>
        <TabsContent value="exports" className="flex min-h-0 flex-col">
          <ExportsTab onExport={() => setExportOpen(true)} />
        </TabsContent>
      </Tabs>

      {exportOpen ? (
        <ExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          initialRange={range}
          requireApproval={kpis.data?.require_approval ?? true}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      ) : null}

      {settingsOpen ? (
        <SettingsModal
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          initialSection="time-billing"
        />
      ) : null}
    </div>
  );
};

export default BillingReviewPage;
